import { useCallback, useEffect, useMemo, useState } from 'react';
import type { InternalTableSchema, VisibilityCondition } from '../core/schema/types';
import { isRowSelectable } from '../utils/rowSelection';

interface UseTableSelectionParams {
  internalSchema: InternalTableSchema;
  displayData: unknown[];
  paginatedData: unknown[];
  controlledSelectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  stateActions: {
    selectRow: (key: string) => void;
    deselectAll: () => void;
  };
  tableStateSelectedKeys: string[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
}

interface UseTableSelectionReturn {
  showSelectionColumn: boolean;
  keyField: string;
  selectedKeysValue: string[];
  effectiveSelectedKeys: string[];
  allVisibleSelected: boolean;
  isPartiallySelected: boolean;
  isAllPagesSelected: boolean;
  uncheckedIds: string[];
  selectableKeys: string[];
  handleSelectionChange: (keys: string[]) => void;
  handleSelectAll: (checked: boolean) => void;
  handleSelectAllPages: () => void;
  handleClearAllPages: () => void;
  handleRowSelect: (rowKey: string, checked: boolean) => void;
}

export function useTableSelection({
  internalSchema,
  displayData,
  paginatedData,
  controlledSelectedKeys,
  onSelectionChange,
  stateActions,
  tableStateSelectedKeys,
  resolveRowKey,
}: UseTableSelectionParams): UseTableSelectionReturn {
  const selection = internalSchema.features.selection;
  const showSelectionColumn = selection.enabled;
  const keyField = selection.keyField || 'id';

  const selectedKeysValue = controlledSelectedKeys ?? tableStateSelectedKeys;

  const handleSelectionChange = useCallback(
    (keys: string[]) => {
      if (onSelectionChange) {
        onSelectionChange(keys);
      } else {
        stateActions.deselectAll();
        keys.forEach((k) => stateActions.selectRow(k));
      }
    },
    [onSelectionChange, stateActions]
  );

  const selectableKeys = useMemo(() => {
    return displayData
      .map((r, i) => {
        const rec = r as Record<string, unknown>;
        const key = resolveRowKey(rec, i);
        const canSelect = isRowSelectable(rec, selection.selectable);
        return canSelect ? key : null;
      })
      .filter((k): k is string => k !== null);
  }, [displayData, resolveRowKey, selection.selectable]);

  const allCurrentPageKeys = useMemo(() => {
    return paginatedData
      .map((r, i) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, i);
      })
      .filter((key) => selectableKeys.includes(key));
  }, [paginatedData, resolveRowKey, selectableKeys]);

  const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);
  const [uncheckedIds, setUncheckedIds] = useState<string[]>([]);

  const effectiveSelectedKeys = useMemo(() => {
    if (!isAllPagesSelected) return selectedKeysValue;
    return allCurrentPageKeys.filter((k) => !uncheckedIds.includes(k));
  }, [isAllPagesSelected, selectedKeysValue, allCurrentPageKeys, uncheckedIds]);

  const allVisibleSelected =
    allCurrentPageKeys.length > 0 &&
    allCurrentPageKeys.every((key) => effectiveSelectedKeys.includes(key));

  const isPartiallySelected =
    !allVisibleSelected && allCurrentPageKeys.some((key) => effectiveSelectedKeys.includes(key));

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (isAllPagesSelected) {
        setIsAllPagesSelected(false);
        setUncheckedIds([]);
      }
      if (checked) {
        const newKeys = Array.from(new Set([...selectedKeysValue, ...allCurrentPageKeys]));
        handleSelectionChange(newKeys);
      } else {
        setIsAllPagesSelected(false);
        setUncheckedIds([]);
        const currentPageSet = new Set(allCurrentPageKeys);
        handleSelectionChange(selectedKeysValue.filter((k) => !currentPageSet.has(k)));
      }
    },
    [selectedKeysValue, allCurrentPageKeys, handleSelectionChange, isAllPagesSelected]
  );

  const handleSelectAllPages = useCallback(() => {
    handleSelectionChange(allCurrentPageKeys);
    setIsAllPagesSelected(true);
    setUncheckedIds([]);
  }, [handleSelectionChange, allCurrentPageKeys]);

  const handleClearAllPages = useCallback(() => {
    handleSelectionChange([]);
    setIsAllPagesSelected(false);
    setUncheckedIds([]);
  }, [handleSelectionChange]);

  useEffect(() => {
    if (!isAllPagesSelected) return;
    const currentPageChecked = allCurrentPageKeys.filter((k) => !uncheckedIds.includes(k));
    handleSelectionChange(currentPageChecked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCurrentPageKeys, uncheckedIds, isAllPagesSelected]);

  const handleRowSelect = useCallback(
    (rowKey: string, checked: boolean) => {
      if (isAllPagesSelected) {
        if (checked) {
          setUncheckedIds((prev) => prev.filter((id) => id !== rowKey));
          handleSelectionChange([...selectedKeysValue, rowKey]);
        } else {
          setUncheckedIds((prev) => [...prev, rowKey]);
          handleSelectionChange(selectedKeysValue.filter((k) => k !== rowKey));
        }
        return;
      }
      if (checked) {
        handleSelectionChange([...selectedKeysValue, rowKey]);
      } else {
        handleSelectionChange(selectedKeysValue.filter((k) => k !== rowKey));
      }
    },
    [selectedKeysValue, handleSelectionChange, isAllPagesSelected]
  );

  return {
    showSelectionColumn,
    keyField,
    selectedKeysValue,
    effectiveSelectedKeys,
    allVisibleSelected,
    isPartiallySelected,
    isAllPagesSelected,
    uncheckedIds,
    selectableKeys,
    handleSelectionChange,
    handleSelectAll,
    handleSelectAllPages,
    handleClearAllPages,
    handleRowSelect,
  };
}
